import { ReactNode } from 'react';
import AvatarChip from './avatar-chip';
import { documentTypeOptions } from './google-document-types';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';

export type TargetType =
  | 'User'
  | 'Group'
  | 'Domain'
  | 'gPanel Role'
  | 'OrgUnit'
  | 'Shared Drive'
  | 'RegEx'
  | 'User Drive'
  | 'Resource Calendar'
  | 'Shared Drive'
  | 'Unknown';

interface AvatarChipItems {
  id?: string;
  email: string;
  type: TargetType;
}
export interface ReviewSectionItem {
  label?: string;
  value: string | number | boolean | string[] | AvatarChipItems[] | ReactNode;
  variant?: string;
  isVisible?: boolean;
  flex?: boolean;
}

export interface ReviewSection {
  title: string;
  headerButton?: ReactNode;
  // Left Column
  leftWidth?: {
    // for column size
    columnWidth?: number;
    // for grid size, labelWidth + valueWidth must be equal to 12
    labelWidth?: number;
    valueWidth?: number;
  };
  // Right Column
  rightWidth?: {
    // for column size
    columnWidth?: number;
    // for grid size, labelWidth + valueWidth must be equal to 12
    labelWidth?: number;
    valueWidth?: number;
  };
  leftItems?: ReviewSectionItem[];
  rightItems?: ReviewSectionItem[];
}

interface SharedReviewLayoutProps {
  sections: ReviewSection[];
}

interface ReviewItemProps {
  item: ReviewSectionItem;
  hasOpposite: boolean;
  widths?: { labelWidth?: number; valueWidth?: number };
}

function ReviewItem({ item, hasOpposite, widths }: ReviewItemProps) {
  const {
    isVisible = true,
    label,
    value,
    variant = 'default',
    flex = false,
  } = item;
  if (!isVisible) return null;

  function displayValue() {
    // For boolean
    if (typeof value === 'boolean') {
      return <Typography>{value ? <Check /> : <Close />}</Typography>;
    } else if (
      // For status enabled/disabled/active
      typeof value === 'string' &&
      (value.toLowerCase() === 'enabled' ||
        value === 'active' ||
        value.toLowerCase() === 'disabled' ||
        value.toLowerCase() === 'draft')
    ) {
      return value && (value === 'Enabled' || value === 'active') ? (
        <Chip
          variant="outlined"
          icon={<Check />}
          label={value}
          color="success"
          size="small"
          sx={{ width: 'fit-content' }}
        />
      ) : value.toLowerCase() === 'disabled' ? (
        <Chip
          variant="outlined"
          icon={<DoNotDisturb />}
          label={value}
          color="warning"
          size="small"
          sx={{ width: 'fit-content' }}
        />
      ) : value.toLowerCase() === 'draft' ? (
        <Chip
          variant="outlined"
          icon={<AccessTimeOutlined />}
          label={value}
          color="info"
          size="small"
          sx={{ width: 'fit-content' }}
        />
      ) : (
        <Typography sx={{ textTransform: 'capitalize' }}>{value}</Typography>
      );
    } else if (typeof value === 'string' || typeof value === 'number') {
      if (variant === 'date' && typeof value === 'number')
        return (
          <Typography key={value} sx={{ wordBreak: 'break-word' }}>
            {new Date(value).toLocaleString()}
          </Typography>
        );
      // For text values
      return (
        <Typography key={value} sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      );
    } else if (Array.isArray(value)) {
      //For array of string
      // For string of fileMimeTypes
      if (typeof value[0] === 'string') {
        if (variant === 'fileType') {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1,
                width: '100%',
              }}
            >
              {value.map((types) => {
                const file = documentTypeOptions.find(
                  (type) => type.value === types
                )!;
                return (
                  <Tooltip key={file.displayValue} title={file.displayValue}>
                    <Chip
                      key={file.displayValue}
                      label={file.displayValue}
                      size="small"
                      avatar={<Avatar src={file.icon} />}
                      sx={{
                        width: 'fit-content',
                        backgroundColor: '-moz-initial',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          );
        } else {
          // For string array displayed vertically
          return (
            <Stack direction="column">
              {value
                .filter((val): val is string => typeof val === 'string')
                .map((val) => (
                  <Typography key={val}>{val}</Typography>
                ))}
            </Stack>
          );
        }
      }
      // For array emails and source/target types
      else if (variant === 'chip') {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
            }}
          >
            {(value as AvatarChipItems[]).map((val) => (
              <AvatarChip
                key={val.id ? val.id : val.email}
                email={val.email}
                type={val.type as TargetType}
              />
            ))}
          </Box>
        );
      } else return value as ReactNode;
    } else return value;
  }

  return (
    <>
      {label && (
        <Grid
          size={{
            xs: hasOpposite ? widths?.labelWidth ?? 5 : widths?.labelWidth ?? 2,
          }}
        >
          <Typography fontWeight="bold">{label}</Typography>
        </Grid>
      )}

      <Grid
        size={{
          xs: flex
            ? 12
            : hasOpposite
            ? label
              ? widths?.valueWidth ?? 7
              : 12
            : label
            ? widths?.valueWidth ?? 10
            : 12,
        }}
        sx={{ minWidth: 0 }}
      >
        {displayValue()}
      </Grid>
    </>
  );
}

export default function SharedReviewLayout({
  sections,
}: SharedReviewLayoutProps) {
  return (
    <Box sx={{ width: '100%' }}>
      {sections.map((section, idx) => {
        const hasVisibleItemsLeft =
          section.leftItems &&
          section.leftItems.some((item) => item.isVisible !== false);

        const hasVisibleItemRight =
          section.rightItems &&
          section.rightItems.some((item) => item.isVisible !== false);

        const hasVisibleItems = hasVisibleItemsLeft || hasVisibleItemRight;

        if (!hasVisibleItems) return null;

        return (
          <Box key={section.title} sx={{ mb: 2 }}>
            {/* Section Title */}
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', mb: 0, mt: idx > 0 ? 2 : 0 }}
            >
              {section.title} {section.headerButton}
            </Typography>

            {/* Section Content */}
            <Box sx={{ p: 2, pr: 0, width: '100%' }}>
              <Grid container spacing={2}>
                {/* Left Column */}
                {hasVisibleItemsLeft && (
                  <Grid
                    size={{
                      xs: 12,
                      lg:
                        section.leftWidth && section.leftWidth.columnWidth
                          ? section.leftWidth.columnWidth
                          : hasVisibleItemRight
                          ? 5.75
                          : 12,
                    }}
                  >
                    <Grid container spacing={2}>
                      {section.leftItems?.map((item, index) => (
                        <ReviewItem
                          // eslint-disable-next-line react-x/no-array-index-key
                          key={`${item.label || 'item'}-${index}`}
                          item={item}
                          hasOpposite={!!hasVisibleItemRight}
                          widths={section.leftWidth}
                        />
                      ))}
                    </Grid>
                  </Grid>
                )}

                {/* Divider */}
                {hasVisibleItemsLeft && hasVisibleItemRight && (
                  <Grid
                    size={{ xs: 0.3 }}
                    sx={{ display: { xs: 'none', lg: 'flex' } }}
                  >
                    <Divider orientation="vertical" flexItem />
                  </Grid>
                )}

                {/* Right Column */}
                {hasVisibleItemRight && (
                  <Grid
                    size={{
                      xs: 12,
                      lg:
                        section.rightWidth && section.rightWidth.columnWidth
                          ? section.rightWidth.columnWidth
                          : hasVisibleItemsLeft
                          ? 5.75
                          : 12,
                    }}
                  >
                    <Grid container spacing={2}>
                      {section.rightItems?.map((item, index) => (
                        <ReviewItem
                          // eslint-disable-next-line react-x/no-array-index-key
                          key={`${item.label || 'item'}-${index}`}
                          item={item}
                          hasOpposite={hasVisibleItemRight}
                          widths={section.rightWidth}
                        />
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
